import Downshift, { DownshiftProps, DownshiftState, StateChangeOptions } from "downshift";
import { matchSorter } from "match-sorter";
import React, { cloneElement, createRef, KeyboardEvent, ReactElement } from "react";
import Box, { BoxProps } from "../box/Box";
import CloseButton from "../CloseButton";
import { useFormControl } from "../form/FormControl";
import Input, { InputProps, useInputStyle } from "../input/Input";

interface IBaseTypeaheadItem {
  value: string;
  label: string;
}

interface IRenderItemProps<T = any> {
  item: T;
  active: boolean;
}

interface IRenderSelectedItemProps<T = any> {
  item: T;
  onRemove: (item: T) => void;
}

export interface IMultiTypeaheadProps<T extends IBaseTypeaheadItem> extends Omit<DownshiftProps<T[]>, "onChange"> {
  items: T[];
  renderSelectedItem: (props: IRenderSelectedItemProps<T>) => ReactElement;
  renderItem: (props: IRenderItemProps<T>) => ReactElement;
  selectedItems: T[];
  onChange: (items: T[]) => void;
  onBlur?: (items: T[]) => void;
  placeholder?: string;
  bg?: string;
  menuProps?: BoxProps;
  inputProps?: InputProps;
  getMenuProps?: () => Record<string, any>;
  getInputProps?: () => Record<string, any>;
  getItemProps?: (globalIndex: number, option: any) => Record<string, any>;
}

function DEFAULT_STATE_REDUCER<T = any>(
  state: DownshiftState<T>,
  changes: StateChangeOptions<T>,
): Partial<StateChangeOptions<T>> {
  switch (changes.type) {
    case Downshift.stateChangeTypes.clickItem:
      return {
        ...changes,
        inputValue: "",
        highlightedIndex: 0,
        isOpen: true,
      };
    case Downshift.stateChangeTypes.keyDownEnter:
      return {
        ...changes,
        inputValue: "",
        highlightedIndex: 0,
        isOpen: true,
      };
    case Downshift.stateChangeTypes.keyDownEscape:
      return {
        ...changes,
        selectedItem: state.selectedItem,
      };
    default:
      return changes;
  }
}

export default function MultiTypeahead<IItem extends IBaseTypeaheadItem>({
  items,
  renderItem,
  renderSelectedItem,
  placeholder,
  onChange,
  onBlur = () => {},
  selectedItems,
  bg = "cyan.100",
  menuProps = {},
  inputProps = {},
  getMenuProps: getMenuPropsFn = () => ({}),
  getInputProps: getInputPropsFn = () => ({}),
  getItemProps: getItemPropsFn = () => ({}),
  ...props
}: IMultiTypeaheadProps<IItem>) {
  const inputRef = createRef<HTMLInputElement>();

  function handleSelection(addedItem: any) {
    if (addedItem) {
      onChange([...selectedItems, addedItem]);
    }
  }

  function handleInputKeyDown(e: KeyboardEvent<HTMLInputElement>, inputValue: string | null) {
    if (e.key === "Backspace" && (!inputValue || inputValue.length === 0)) {
      onChange(selectedItems.slice(0, -1));
    }
  }

  function handleRemove(item: IItem) {
    onChange(selectedItems.filter(({ value }) => value !== item.value));

    if (inputRef.current) {
      inputRef.current.focus();
    }
  }

  function getInputPaddingBottom(isOpen: boolean, selectedItems: IItem[] | null) {
    if (isOpen) {
      return 2;
    }

    return selectedItems && selectedItems.length > 0 ? 0 : "7px";
  }

  const inputStyle = useInputStyle({ variant: "outline", errorBorderColor: "red.600", ...inputProps });
  const formControl = useFormControl(inputProps);

  return (
    <Downshift<IItem[]>
      itemToString={() => ""}
      onChange={handleSelection}
      stateReducer={DEFAULT_STATE_REDUCER}
      selectedItem={selectedItems}
      {...props}
    >
      {({
        getMenuProps,
        getItemProps,
        getInputProps,
        getRootProps,
        isOpen,
        toggleMenu,
        highlightedIndex,
        selectedItem,
        inputValue,
      }) => (
        <div {...getRootProps(undefined, { suppressRefError: true })}>
          <Box
            boxSizing="border-box"
            // @ts-ignore
            position="relative"
            border="1px"
            aria-readonly={!!inputProps.readOnly}
            aria-label={inputProps["aria-label"]}
            aria-invalid={!!formControl.invalid}
            aria-required={!!formControl.required}
            aria-disabled={!!formControl.disabled}
            aria-describedby={inputProps["aria-describedby"]}
            {...inputProps}
            {...inputStyle}
            borderColor={isOpen ? "blue.600" : "gray.100"}
            _hover={{
              borderColor: !isOpen ? "gray.300" : null,
            }}
            _focus={{
              borderColor: "gray.300",
            }}
            height="auto"
            rounded="md"
            roundedBottom={isOpen ? 0 : "md"}
            borderBottom={isOpen ? 0 : "1px"}
            borderBottomColor={isOpen ? "blue.600" : "gray.100"}
            display="flex"
            flexWrap="wrap"
            px={3}
            py="7px"
            pb={getInputPaddingBottom(isOpen, selectedItem)}
            backgroundColor="white"
            maxWidth="100%"
          >
            {(selectedItem || []).map((item) => (
              <Box key={item.value} mr={2} mb={2} d="inline-flex" cursor="pointer" maxWidth="100%">
                {renderSelectedItem({
                  item,
                  onRemove: handleRemove,
                })}
              </Box>
            ))}

            <Input
              display="inline-flex"
              border={0}
              flex={1}
              _focus={{
                border: 0,
              }}
              tabIndex={0}
              width="auto"
              minWidth="225px"
              boxSizing="border-box"
              height="24px"
              p={0}
              mb={selectedItem && selectedItem.length > 0 ? 2 : 0}
              // @ts-ignore
              ref={inputRef}
              placeholder={placeholder}
              {...getInputProps({
                ...getInputPropsFn(),
                onFocus: () => {
                  toggleMenu();
                },
                onBlur: () => onBlur(selectedItems),
                onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => handleInputKeyDown(e, inputValue),
                size: undefined,
                as: "input",
              })}
            />
            {selectedItem && (
              <CloseButton
                position="absolute"
                right={0}
                top={0}
                onClick={() => {
                  onChange([]);

                  if (inputRef.current) {
                    inputRef.current.focus();
                  }
                }}
                height="100%"
              />
            )}
          </Box>

          {isOpen && (
            <div css={{ position: "relative" }}>
              <Box
                maxWidth="100%"
                as="ul"
                position="relative"
                top={0}
                left={0}
                border="1px"
                // maxHeight={500}
                borderColor={formControl.invalid ? "red.600" : "blue.600"}
                rounded="md"
                roundedTop={0}
                borderTop={0}
                zIndex="typeahead"
                p={3}
                pb={2}
                pt={4}
                bg={formControl.invalid ? "red.50" : bg}
                listStyleType="none"
                display="block"
                width="100%"
                {...menuProps}
                {...getMenuProps(getMenuPropsFn())}
              >
                <Box
                  position="relative"
                  _after={{
                    bottom: "16px",
                    left: "12px",
                    // @ts-ignore
                    content: "' '",
                    position: "absolute",
                    pointerEvents: "none",
                    width: "0",
                    height: "0",
                    borderStyle: "solid",
                    borderWidth: "0 12px 10px 12px",
                    borderColor: "transparent",
                    borderBottomColor: formControl.invalid ? "red.50" : bg,
                  }}
                />
                {matchSorter(items, inputValue || "", { keys: ["label"] })
                  .filter(({ value }) => !(selectedItem || []).find(({ value: maybeValue }) => maybeValue === value))
                  .map((item, index) => (
                    <Box mr={2} mb={2} d="inline-flex" cursor="pointer" key={item.value} maxWidth="100%">
                      {cloneElement(
                        renderItem({
                          item,
                          active: highlightedIndex === index,
                        }),
                        {
                          ...getItemProps({
                            ...getItemPropsFn(index, item),
                            item: item as any,
                            key: item.value,
                            index,
                          }),
                        },
                      )}
                    </Box>
                  ))}
              </Box>
            </div>
          )}
        </div>
      )}
    </Downshift>
  );
}
